// components/AxiosUtils.ts
import axios from "axios";
import { GetBaseAPIUrl } from "../components/Utils";

const api = axios.create({
  baseURL: GetBaseAPIUrl(),
  withCredentials: true,
});

// --- Token + "ready" gate -----------------------------
let authToken: string | null = null;

// A promise that resolves when a token is first set.
// Early requests will await this so they don't race.
let resolveReady: (() => void) | null = null;
let tokenReady = new Promise<void>((resolve) => {
  resolveReady = resolve;
});

/** Call this when you obtain (or clear) the token. */
export function setAuthToken(token: string | null) {
  authToken = token ?? null;

  // If this is the first time we set a token (or explicitly clearing),
  // unblock any queued requests waiting for initial token.
  if (resolveReady) {
    resolveReady();
    resolveReady = null;
  }
}

// If you support logout/login cycles and want new requests to wait again
// until a *new* token is set, use this helper on logout.
export function resetTokenGate() {
  // create a fresh gate and keep current token as-is (likely null on logout)
  tokenReady = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
}

// --- Interceptor: wait for token, then attach -----------
api.interceptors.request.use(async (config) => {
  // If no token yet, wait until TokenBootstrap sets it (or clears it).
  if (!authToken && resolveReady) {
    await tokenReady; // resolves once setAuthToken() is called
  }

  if (!config.headers) config.headers = {};
  if (authToken) {
    (config.headers as any)["Authorization"] = `Bearer ${authToken}`;
  } else {
    // ensure header is not set when no token
    delete (config.headers as any)["Authorization"];
  }

  return config;
});

export default api;
